import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from '@/contexts/AuthContext'
import IndustryStandardLayout from '@/components/IndustryStandardLayout'
import ProtectedRoute from '@/components/ProtectedRoute'
import LoginPage from '@/pages/Login'
import WelcomePage from '@/pages/Welcome'
import { AuthCallback } from '@/pages/AuthCallback'
import Dashboard from '@/pages/Dashboard'
import CertificatesPage from '@/pages/foundation/CertificatesPage'
import CodecGroupsPage from '@/pages/foundation/CodecGroupsPage'
import MediaRealmsPage from '@/pages/foundation/MediaRealmsPage'
import TlsContextsPage from '@/pages/foundation/TlsContextsPage'
import HostsPage from '@/pages/foundation/HostsPage'
import PeerGroupsPage from '@/pages/connectivity/PeerGroupsPage'
import { SipRegistrationsPage } from '@/pages/registrations/SipRegistrationsPage'
import BackupRestorePage from '@/pages/BackupRestorePage'
import { SipConfigurationPage } from '@/pages/sip/SipConfigurationPage'
import { SipManipulationsPage } from '@/pages/sip/SipManipulationsPage'
import SipProfilesPage from '@/pages/connectivity/SipProfilesPage'
import { LogsPage } from '@/pages/system/LogsPage'
import { SipTrafficLogsPage } from '@/pages/system/SipTrafficLogsPage'
import { CallHistoryPage } from '@/pages/system/CallHistoryPage'
import { OngoingCallsPage } from '@/pages/system/OngoingCallsPage'
import { PushGatewayPage } from '@/pages/system/PushGatewayPage'
import { ContainersPage } from '@/pages/system/ContainersPage'
import { ContainerDetailPage } from '@/pages/system/ContainerDetailPage'
import { ContainerProfilesPage } from '@/pages/system/ContainerProfilesPage'
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage'
import { GuidedScenarioWorkflowPage } from '@/pages/onboarding/GuidedScenarioWorkflowPage'
import { SipInterfacesPage } from '@/pages/admin/network/SipInterfacesPage'
import RoutingRulesPage from '@/pages/intelligence/RoutingRulesPage'
import { UserSettingsPage } from '@/pages/settings/UserSettingsPage'
import { AdminSettingsPage } from '@/pages/settings/AdminSettingsPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import SipAuthenticationPage from '@/pages/security/SipAuthenticationPage'
import BusinessCallHandlingPage from '@/pages/intelligence/BusinessCallHandlingPage'
import { useEffect } from 'react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
})

function AuthErrorHandler() {
  const location = useLocation()
  const navigate = useNavigate()
  
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const error = params.get('error')
    const errorDescription = params.get('error_description')
    
    if (error) {
      console.error('Auth error detected:', error, errorDescription)
      
      sessionStorage.removeItem('challenge')
      sessionStorage.removeItem('login_in_progress')
      
      navigate('/login', { 
        state: { 
          error: errorDescription || error,
          from: location.pathname 
        },
        replace: true 
      })
    }
  }, [location, navigate])
  
  return null
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthErrorHandler />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <IndustryStandardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="welcome" element={<WelcomePage />} />
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="onboarding/:path" element={<GuidedScenarioWorkflowPage />} />
            
            {/* Foundation Configuration Pages */}
            <Route path="foundation/certificates" element={<CertificatesPage />} />
            <Route path="foundation/codec-groups" element={<CodecGroupsPage />} />
            <Route path="foundation/media-realms" element={<MediaRealmsPage />} />
            <Route path="foundation/tls-contexts" element={<TlsContextsPage />} />
            <Route path="foundation/hosts" element={<HostsPage />} />
            <Route path="foundation/sip-interfaces" element={<SipInterfacesPage />} />
            
            {/* Security Layer */}
            <Route path="security/sip-authentication" element={<SipAuthenticationPage />} />
            
            {/* Connectivity Layer */}
            <Route path="connectivity/sip-profiles" element={<SipProfilesPage />} />
            <Route path="connectivity/peer-groups" element={<PeerGroupsPage />} />
            <Route path="connectivity/sip-peers" element={<SipConfigurationPage />} />
            <Route path="connectivity/sip-registrations" element={<SipRegistrationsPage />} />
            <Route path="connectivity/sip-manipulations" element={<SipManipulationsPage />} />
            <Route path="intelligence/business-call-handling" element={<BusinessCallHandlingPage />} />
            <Route path="intelligence/routing-rules" element={<RoutingRulesPage />} />

            {/* System Layer */}
            <Route path="system/ongoing-calls" element={<OngoingCallsPage />} />
            <Route path="system/containers" element={<ContainersPage />} />
            <Route path="system/containers/:containerId" element={<ContainerDetailPage />} />
            <Route path="system/container-profiles" element={<ContainerProfilesPage />} />
            <Route path="system/push-gateway" element={<PushGatewayPage />} />
            <Route path="system/logs" element={<LogsPage />} />
            <Route path="system/sip-traffic-logs" element={<SipTrafficLogsPage />} />
            <Route path="system/call-history" element={<CallHistoryPage />} />
            
            <Route path="backup-restore" element={<BackupRestorePage />} />
            
            {/* Settings */}
            <Route path="settings/user" element={<UserSettingsPage />} />
            <Route path="settings/system" element={<AdminSettingsPage />} />
          </Route>
        </Routes>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default App
